import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { isEqual, get } from 'lodash-es';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { Link } from 'site-modules/shared/components/link/link';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { UserProfileModel } from 'client/data/models/profile/profile';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { EventToolbox } from 'client/utils/event-toolbox';
import { PAGE_CONTENT_UPDATE, TrackingConstant } from 'client/tracking/constant';
import { EngagementHandler } from 'client/engagement-handlers/index';
import { ListEngagementHandler } from 'client/engagement-handlers/list-engagement-handler/list-engagement-handler';

import { RecentlyViewedCard } from './recently-viewed-card';
import './recently-viewed-vins.scss';

EngagementHandler.register(ListEngagementHandler);

/**
 * Shows section with recently viewed vins.
 */
function RecentlyViewedVinsComponent({
  recentlyViewedVins = [],
  compareSelect = null,
  isMobile = false,
  visitorLocation = null,
}) {
  const isTrackingFired = useRef(false);
  const hasVins = !!recentlyViewedVins.length;

  useEffect(() => {
    if (!isTrackingFired.current && hasVins) {
      EventToolbox.fireCustomEvent(TrackingConstant.ACTION_LIST_VIEWED, {
        inventory: recentlyViewedVins,
        creativeId: 'edm-entry-recently-viewed',
      });
      isTrackingFired.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVins]);

  useEffect(() => {
    EventToolbox.fireCustomEvent(PAGE_CONTENT_UPDATE);
  }, []);

  if (!hasVins) {
    return null;
  }

  return (
    <section className="recently-viewed mt-2">
      <h2 className="heading-3 mb-1" id="rv-list">
        Recently viewed
      </h2>
      <Row tag="ul" className="list-unstyled" aria-labelledby="rv-list">
        {recentlyViewedVins.map((vinData, index) => (
          <Col tag="li" key={vinData.vin} xs={6} md={3}>
            <RecentlyViewedCard
              vinData={vinData}
              compareSelect={compareSelect}
              visitorLocation={visitorLocation}
              isMobile={isMobile}
              trackingId="view_recently_viewed_vins"
              trackingValue={`${index},${vinData.vin}`}
            />
          </Col>
        ))}
      </Row>
      <Link to="/inventory/srp.html" className="text-primary-darker mt-1 ms-0_5">
        Find more cars like these <span className="icon-arrow-right3 legal text-primary-darker" aria-hidden />
      </Link>
    </section>
  );
}

RecentlyViewedVinsComponent.propTypes = {
  recentlyViewedVins: PropTypes.arrayOf(PropTypes.shape({})),
  isMobile: PropTypes.bool,
  visitorLocation: VisitorEntities.Location,
  compareSelect: PropTypes.shape({}),
};

export const propsAreEqual = (
  { recentlyViewedVins: prevInventory, compareSelect: prevCompareSelect },
  { recentlyViewedVins: nextInventory, compareSelect: nextCompareSelect }
) => {
  const prevInventoryLength = get(prevInventory, 'length');
  const nextInventoryLength = get(nextInventory, 'length');

  // to prevent updating when cards are removing
  if (prevInventoryLength && !nextInventoryLength) {
    return true;
  }

  const inventoryChanged = prevInventoryLength !== nextInventoryLength || !isEqual(prevInventory, nextInventory);
  const compareSelectedCarsChanged =
    nextCompareSelect && prevCompareSelect && !isEqual(prevCompareSelect.selectedCars, nextCompareSelect.selectedCars);

  return !(inventoryChanged || compareSelectedCarsChanged);
};

const mapStateToProps = state => ({
  isAuthenticated: state.profile.isAuthenticated,
  isMobile: state.mobile,
});

export const RecentlyViewedVins = connect(mapStateToProps)(
  connectToModel(React.memo(RecentlyViewedVinsComponent, propsAreEqual), {
    recentlyViewedVins: bindToPath(
      props => (props.isAuthenticated ? `data.recentlyViewedVins.size["4"]` : ''),
      UserProfileModel
    ),
    visitorLocation: bindToPath('location', VisitorModel),
  })
);
